import { createElement } from 'react';
import PropTypes from 'prop-types';
import { className } from '../../../utils/GlobalCssProxy';

/**
 * A wrapper around the Bootstrap ```Card``` component,
 * used in lieu of the ```react-bootstrap``` component,
 * which applies BS utility classes without namespacing.
 *
 * The ```BsCard``` component includes the following sub-components:
 * * ```BsCard.Header```
 * * ```BsCard.Body```
 * * ```BsCard.Title```
 * * ```BsCard.Subtitle```
 * * ```BsCard.Text```
 * * ```BsCard.Footer```
 *
 * The tag used to render each sub-component can be customized using the ```as``` prop,
 * and the ```className``` prop will be passed to the root.
 */
const BsCard = ({ children, className: classNameProp, ...props }) => {
  const classes = [className.string('card', 'card-print'), classNameProp]
    .filter(Boolean)
    .join(' ');

  return <div className={classes} {...props}>{children}</div>;
};

BsCard.propTypes = {
  /**
   * Content to be rendered inside the card
   */
  children: PropTypes.node,
  /**
   * Additional HTML classes to be applied to the card root node
   */
  className: PropTypes.string
};

BsCard.Header = createSubComponent({
  classes: ['card-header']
});

BsCard.Body = createSubComponent({
  classes: ['card-body']
});

BsCard.Title = createSubComponent({
  classes: ['card-title'],
  defaultTag: 'h4'
});

BsCard.Subtitle = createSubComponent({
  classes: ['card-subtitle'],
  defaultTag: 'span'
});

BsCard.Text = createSubComponent({
  classes: ['card-text'],
  defaultTag: 'p'
});

BsCard.Footer = createSubComponent({
  classes: ['card-footer']
});

export default BsCard;

function createSubComponent({ defaultTag = 'div', classes }) {
  const component = props => {
    const newProps = Object.assign({}, props, {
      className: [props.className, className.string(...classes)].join(' '),
      as: undefined
    });
    return createElement(props.as, newProps);
  };

  component.propTypes = {
    // children: PropTypes.node,
    className: PropTypes.string,
    as: PropTypes.string
  };

  component.defaultProps = {
    as: defaultTag
  };

  return component;
}
